import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";
import { clearAuthData } from "../utils/auth";
import type { User, ApiResponse } from "../types/shared.types";
import type {
  SubmitFeedbackPayload,
  UpdateFeedbackPayload,
  FeedbackListResponse,
  FeedbackStatsResponse,
  FeedbackDetailResponse,
  FeedbackSubmitResponse,
} from "../types/feedback.types";

// API configuration for RecruitmentMaster
const defaultBackendPath = "/api";
const backendPort = process.env.REACT_APP_BACKEND_PORT || "5000";
const baseURL =
  process.env.NODE_ENV === "development"
    ? `http://${window.location.hostname}:${backendPort}${defaultBackendPath}`
    : `https://${window.location.hostname}${defaultBackendPath}`;

const api: AxiosInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Required for HTTP-only cookies
});

// Request interceptor - no need to add Authorization header
// Cookies are sent automatically with withCredentials: true
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => config,
  (error) => Promise.reject(error),
);

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };
    const requestUrl = originalRequest.url || "";

    // Never attempt token refresh on auth endpoints themselves
    const isAuthEndpoint =
      requestUrl.includes("/auth/login") ||
      requestUrl.includes("/onboarding/register") ||
      requestUrl.includes("/auth/refresh") ||
      requestUrl.includes("/auth/forgot-password") ||
      requestUrl.includes("/auth/reset-password");

    // If we get 401 and haven't tried refreshing yet (skip auth endpoints)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        await api.post("/auth/refresh");
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect to login
        const currentPath = window.location.pathname;
        if (
          currentPath !== "/login" &&
          !currentPath.startsWith("/onboarding")
        ) {
          clearAuthData();
          // Dispatch event so React components can handle navigation via router
          window.dispatchEvent(new CustomEvent("auth-logout"));
        }
        throw refreshError;
      }
    }

    // Handle 403 (Forbidden) - just reject, let component handle it
    throw error;
  },
);

// Type definitions for API responses
interface LoginCredentials {
  username: string;
  password: string;
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
}

interface EmailData {
  to: string | string[];
  subject: string;
  body: string;
  cc?: string[];
  bcc?: string[];
}

interface TodoFilters {
  status?: string;
  priority?: string;
  relatedType?: string;
  relatedId?: number | string;
  candidateId?: number;
  dueDateFrom?: string;
  dueDateTo?: string;
  page?: number;
  limit?: number;
}

interface TemplateFilters {
  includeOrganization?: boolean;
  organizationOnly?: boolean;
  category?: string;
}

// Authentication API functions
const authAPI = {
  login: async (credentials: LoginCredentials): Promise<ApiResponse<User>> => {
    try {
      const response = await api.post("/auth/login", credentials);
      return response.data;
    } catch (error) {
      console.error("Error in login:", error);
      throw error;
    }
  },

  logout: async (): Promise<ApiResponse<void>> => {
    try {
      const response = await api.post("/auth/logout");
      clearAuthData();
      return response.data;
    } catch (error) {
      console.error("Error in logout:", error);
      // Clear auth data even if request fails
      clearAuthData();
      throw error;
    }
  },

  refreshToken: async (): Promise<unknown> => {
    try {
      const response = await api.post("/auth/refresh");
      return response.data;
    } catch (error) {
      console.error("Error in refreshToken:", error);
      throw error;
    }
  },

  getCurrentUser: async (): Promise<ApiResponse<User>> => {
    try {
      const response = await api.get("/auth/me");
      return response.data;
    } catch (error) {
      console.error("Error in getCurrentUser:", error);
      throw error;
    }
  },

  forgotPassword: async (email: string): Promise<ApiResponse<void>> => {
    try {
      const response = await api.post("/auth/forgot-password", { email });
      return response.data;
    } catch (error) {
      console.error("Error in forgotPassword:", error);
      throw error;
    }
  },

  resetPassword: async (
    token: string,
    password: string,
  ): Promise<ApiResponse<void>> => {
    try {
      const response = await api.post("/auth/reset-password", {
        token,
        password,
      });
      return response.data;
    } catch (error) {
      console.error("Error in resetPassword:", error);
      throw error;
    }
  },
};

// User API functions
const userAPI = {
  getProfile: async (): Promise<unknown> => {
    try {
      const response = await api.get("/users/profile");
      return response.data;
    } catch (error) {
      console.error("Error in getProfile:", error);
      throw error;
    }
  },

  updateProfile: async (userData: Partial<User>): Promise<unknown> => {
    try {
      const response = await api.put("/users/profile", userData);
      return response.data;
    } catch (error) {
      console.error("Error in updateProfile:", error);
      throw error;
    }
  },

  changePassword: async (passwordData: PasswordData): Promise<unknown> => {
    try {
      const response = await api.put("/users/change-password", passwordData);
      return response.data;
    } catch (error) {
      console.error("Error in changePassword:", error);
      throw error;
    }
  },

  getNotifications: async (unreadOnly: boolean = false): Promise<unknown> => {
    try {
      const response = await api.get(
        `/users/notifications?unreadOnly=${unreadOnly}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error in getNotifications:", error);
      throw error;
    }
  },

  markNotificationAsRead: async (notificationId: number): Promise<unknown> => {
    try {
      const response = await api.put(
        `/users/notifications/${notificationId}/read`,
      );
      return response.data;
    } catch (error) {
      console.error("Error in markNotificationAsRead:", error);
      throw error;
    }
  },

  markAllNotificationsAsRead: async (): Promise<unknown> => {
    try {
      const response = await api.put("/users/notifications/read-all");
      return response.data;
    } catch (error) {
      console.error("Error in markAllNotificationsAsRead:", error);
      throw error;
    }
  },
};

// Configuration API functions
const configAPI = {
  getConfig: async (): Promise<unknown> => {
    try {
      const response = await api.get("/config");
      return response.data;
    } catch (error) {
      console.error("Error in getConfig:", error);
      throw error;
    }
  },

  saveConfig: async (config: Record<string, unknown>): Promise<unknown> => {
    try {
      const response = await api.post("/config", config);
      return response.data;
    } catch (error) {
      console.error("Error in saveConfig:", error);
      throw error;
    }
  },

  getUserConfig: async (): Promise<unknown> => {
    try {
      const response = await api.get("/config/user");
      return response.data;
    } catch (error) {
      console.error("Error in getUserConfig:", error);
      throw error;
    }
  },

  saveUserConfig: async (config: Record<string, unknown>): Promise<unknown> => {
    try {
      const response = await api.post("/config/user", config);
      return response.data;
    } catch (error) {
      console.error("Error in saveUserConfig:", error);
      throw error;
    }
  },
};

// OpenAI API functions
const openaiAPI = {
  generateText: async (
    prompt: string,
    options: Record<string, unknown> = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post("/openai/generate-text", {
        prompt,
        options,
      });
      return response.data;
    } catch (error) {
      console.error("Error in generateText:", error);
      throw error;
    }
  },

  generateStructuredResponse: async (
    prompt: string,
    schema: unknown,
    options: Record<string, unknown> = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post("/openai/structured-response", {
        prompt,
        schema,
        options,
      });
      return response.data;
    } catch (error) {
      console.error("Error in generateStructuredResponse:", error);
      throw error;
    }
  },

  classifyContent: async (
    content: string,
    categories: string[],
    options: Record<string, unknown> = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post("/openai/classify", {
        content,
        categories,
        options,
      });
      return response.data;
    } catch (error) {
      console.error("Error in classifyContent:", error);
      throw error;
    }
  },

  translateText: async (
    text: string,
    targetLanguage: string,
    options: Record<string, unknown> = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post("/openai/translate", {
        text,
        targetLanguage,
        options,
      });
      return response.data;
    } catch (error) {
      console.error("Error in translateText:", error);
      throw error;
    }
  },

  generateEmail: async (
    emailData: unknown,
    options: Record<string, unknown> = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post("/openai/generate-email", {
        emailData,
        options,
      });
      return response.data;
    } catch (error) {
      console.error("Error in generateEmail:", error);
      throw error;
    }
  },

  summarizeContent: async (
    content: string,
    options: Record<string, unknown> = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post("/openai/summarize", {
        content,
        options,
      });
      return response.data;
    } catch (error) {
      console.error("Error in summarizeContent:", error);
      throw error;
    }
  },

  extractInformation: async (
    text: string,
    fields: string[],
    options: Record<string, unknown> = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post("/openai/extract", {
        text,
        fields,
        options,
      });
      return response.data;
    } catch (error) {
      console.error("Error in extractInformation:", error);
      throw error;
    }
  },

  generateTags: async (
    content: string,
    contextType: string,
    existingTags: string[] = [],
    options: Record<string, unknown> = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post("/openai/generate-tags", {
        content,
        contextType,
        existingTags,
        options,
      });
      return response.data;
    } catch (error) {
      console.error("Error in generateTags:", error);
      throw error;
    }
  },
};

// Email API functions
const emailAPI = {
  sendEmail: async (emailData: EmailData): Promise<unknown> => {
    try {
      const response = await api.post("/email/send", emailData);
      return response.data;
    } catch (error) {
      console.error("Error in sendEmail:", error);
      throw error;
    }
  },

  sendBulkEmails: async (emails: EmailData[]): Promise<unknown> => {
    try {
      const response = await api.post("/email/send-bulk", { emails });
      return response.data;
    } catch (error) {
      console.error("Error in sendBulkEmails:", error);
      throw error;
    }
  },

  sendTemplatedEmail: async (
    emailData: EmailData,
    templateName: string,
    templateData: Record<string, unknown>,
  ): Promise<unknown> => {
    try {
      const response = await api.post("/email/send-templated", {
        emailData,
        templateName,
        templateData,
      });
      return response.data;
    } catch (error) {
      console.error("Error in sendTemplatedEmail:", error);
      throw error;
    }
  },

  sendNotificationEmail: async (
    notificationData: unknown,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        "/email/send-notification",
        notificationData,
      );
      return response.data;
    } catch (error) {
      console.error("Error in sendNotificationEmail:", error);
      throw error;
    }
  },

  sendWelcomeEmail: async (userData: Partial<User>): Promise<unknown> => {
    try {
      const response = await api.post("/email/send-welcome", userData);
      return response.data;
    } catch (error) {
      console.error("Error in sendWelcomeEmail:", error);
      throw error;
    }
  },

  sendPasswordResetEmail: async (resetData: {
    email: string;
  }): Promise<unknown> => {
    try {
      const response = await api.post("/email/send-password-reset", resetData);
      return response.data;
    } catch (error) {
      console.error("Error in sendPasswordResetEmail:", error);
      throw error;
    }
  },

  sendVerificationEmail: async (verificationData: {
    email: string;
  }): Promise<unknown> => {
    try {
      const response = await api.post(
        "/email/send-verification",
        verificationData,
      );
      return response.data;
    } catch (error) {
      console.error("Error in sendVerificationEmail:", error);
      throw error;
    }
  },

  testEmailConfiguration: async (): Promise<unknown> => {
    try {
      const response = await api.get("/email/test");
      return response.data;
    } catch (error) {
      console.error("Error in testEmailConfiguration:", error);
      throw error;
    }
  },
};

// File Upload API functions
const fileAPI = {
  uploadFile: async (
    file: File,
    uploadType: string = "general",
  ): Promise<unknown> => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("uploadType", uploadType);

      const response = await api.post("/files/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error in uploadFile:", error);
      throw error;
    }
  },

  getUserFiles: async (): Promise<unknown> => {
    try {
      const response = await api.get("/files/user");
      return response.data;
    } catch (error) {
      console.error("Error in getUserFiles:", error);
      throw error;
    }
  },

  deleteFile: async (fileId: number | string): Promise<unknown> => {
    try {
      const response = await api.delete(`/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error("Error in deleteFile:", error);
      throw error;
    }
  },

  downloadFile: async (fileId: number | string): Promise<Blob> => {
    try {
      const response = await api.get(`/files/${fileId}/download`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("Error in downloadFile:", error);
      throw error;
    }
  },
};

// Admin API functions
const adminAPI = {
  getAllUsers: async (): Promise<User[]> => {
    try {
      const response = await api.get("/admin/users");
      // Backend returns { success, users, count } - extract users array
      return response.data.users || [];
    } catch (error) {
      console.error("Error in getAllUsers:", error);
      throw error;
    }
  },

  createUser: async (
    userData: Partial<User> & { password: string },
  ): Promise<unknown> => {
    try {
      const response = await api.post("/admin/users", userData);
      return response.data;
    } catch (error) {
      console.error("Error in createUser:", error);
      throw error;
    }
  },

  updateUser: async (
    userId: number,
    userData: Partial<User>,
  ): Promise<unknown> => {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error("Error in updateUser:", error);
      throw error;
    }
  },

  deleteUser: async (userId: number): Promise<unknown> => {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error in deleteUser:", error);
      throw error;
    }
  },

  getAuditLogs: async (
    userId: number | null = null,
    limit: number = 100,
  ): Promise<unknown> => {
    try {
      const params = new URLSearchParams();
      if (userId) params.append("userId", String(userId));
      if (limit) params.append("limit", String(limit));

      const response = await api.get(`/admin/audit-logs?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Error in getAuditLogs:", error);
      throw error;
    }
  },

  getSystemStatus: async (): Promise<unknown> => {
    try {
      const response = await api.get("/admin/system-status");
      return response.data;
    } catch (error) {
      console.error("Error in getSystemStatus:", error);
      throw error;
    }
  },

  // Skills Management
  getSkills: async (category: string | null = null): Promise<unknown> => {
    try {
      const params = category ? { category } : {};
      const response = await api.get("/admin/skills", { params });
      return response.data;
    } catch (error) {
      console.error("Error in getSkills:", error);
      throw error;
    }
  },

  getSkillCategories: async (): Promise<unknown> => {
    try {
      const response = await api.get("/admin/skills/categories");
      return response.data;
    } catch (error) {
      console.error("Error in getSkillCategories:", error);
      throw error;
    }
  },

  createSkill: async (data: {
    name: string;
    category: string;
  }): Promise<unknown> => {
    try {
      const response = await api.post("/admin/skills", data);
      return response.data;
    } catch (error) {
      console.error("Error in createSkill:", error);
      throw error;
    }
  },

  updateSkill: async (
    id: number,
    data: { name?: string; category?: string; is_active?: boolean },
  ): Promise<unknown> => {
    try {
      const response = await api.put(`/admin/skills/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("Error in updateSkill:", error);
      throw error;
    }
  },

  deleteSkill: async (id: number): Promise<unknown> => {
    try {
      const response = await api.delete(`/admin/skills/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error in deleteSkill:", error);
      throw error;
    }
  },

  // Department Management
  getDepartments: async (): Promise<unknown> => {
    try {
      const response = await api.get("/admin/departments");
      return response.data;
    } catch (error) {
      console.error("Error in getDepartments:", error);
      throw error;
    }
  },

  getDepartmentTypes: async (): Promise<unknown> => {
    try {
      const response = await api.get("/admin/departments/types");
      return response.data;
    } catch (error) {
      console.error("Error in getDepartmentTypes:", error);
      throw error;
    }
  },

  getDepartment: async (id: number): Promise<unknown> => {
    try {
      const response = await api.get(`/admin/departments/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error in getDepartment:", error);
      throw error;
    }
  },

  getDepartmentMembers: async (id: number): Promise<unknown> => {
    try {
      const response = await api.get(`/admin/departments/${id}/members`);
      return response.data;
    } catch (error) {
      console.error("Error in getDepartmentMembers:", error);
      throw error;
    }
  },

  createDepartment: async (data: {
    name: string;
    type: string;
    description?: string;
  }): Promise<unknown> => {
    try {
      const response = await api.post("/admin/departments", data);
      return response.data;
    } catch (error) {
      console.error("Error in createDepartment:", error);
      throw error;
    }
  },

  updateDepartment: async (
    id: number,
    data: { name?: string; type?: string; description?: string },
  ): Promise<unknown> => {
    try {
      const response = await api.put(`/admin/departments/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("Error in updateDepartment:", error);
      throw error;
    }
  },

  deleteDepartment: async (id: number): Promise<unknown> => {
    try {
      const response = await api.delete(`/admin/departments/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error in deleteDepartment:", error);
      throw error;
    }
  },
};

// Health check
const healthAPI = {
  checkHealth: async (): Promise<unknown> => {
    try {
      const response = await api.get("/health");
      return response.data;
    } catch (error) {
      console.error("Error in checkHealth:", error);
      throw error;
    }
  },
};

// Communication API functions
const communicationAPI = {
  // Templates
  getTemplates: async (filters: TemplateFilters = {}): Promise<unknown> => {
    try {
      const params: Record<string, unknown> = { ...filters };
      if (filters.includeOrganization !== undefined) {
        params.includeOrganization = filters.includeOrganization;
      }
      if (filters.organizationOnly !== undefined) {
        params.organizationOnly = filters.organizationOnly;
      }
      const response = await api.get("/communication/templates", { params });
      return response.data;
    } catch (error) {
      console.error("Error getting templates:", error);
      throw error;
    }
  },

  getTemplate: async (id: number | string): Promise<unknown> => {
    try {
      const response = await api.get(`/communication/templates/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error getting template:", error);
      throw error;
    }
  },

  createTemplate: async (templateData: unknown): Promise<unknown> => {
    try {
      const response = await api.post("/communication/templates", templateData);
      return response.data;
    } catch (error) {
      console.error("Error creating template:", error);
      throw error;
    }
  },

  updateTemplate: async (
    id: number | string,
    templateData: unknown,
  ): Promise<unknown> => {
    try {
      const response = await api.put(
        `/communication/templates/${id}`,
        templateData,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating template:", error);
      throw error;
    }
  },

  deleteTemplate: async (id: number | string): Promise<unknown> => {
    try {
      const response = await api.delete(`/communication/templates/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting template:", error);
      throw error;
    }
  },

  generateEmailFromTemplate: async (
    templateId: number | string,
    variables: Record<string, unknown> = {},
    useAI: boolean = false,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/communication/templates/${templateId}/generate`,
        {
          variables,
          useAI,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error generating email from template:", error);
      throw error;
    }
  },

  // Recruiter emails
  generateRecruiterEmail: async (data: unknown): Promise<unknown> => {
    try {
      const response = await api.post(
        "/communication/recruiter/generate",
        data,
      );
      return response.data;
    } catch (error) {
      console.error("Error generating recruiter email:", error);
      throw error;
    }
  },

  sendRecruiterEmail: async (data: unknown): Promise<unknown> => {
    try {
      const response = await api.post("/communication/recruiter/send", data);
      return response.data;
    } catch (error) {
      console.error("Error sending recruiter email:", error);
      throw error;
    }
  },

  // Sales emails
  generateSalesEmail: async (data: unknown): Promise<unknown> => {
    try {
      const response = await api.post("/communication/sales/generate", data);
      return response.data;
    } catch (error) {
      console.error("Error generating sales email:", error);
      throw error;
    }
  },

  sendSalesEmail: async (data: unknown): Promise<unknown> => {
    try {
      const response = await api.post("/communication/sales/send", data);
      return response.data;
    } catch (error) {
      console.error("Error sending sales email:", error);
      throw error;
    }
  },

  // Email history
  getEmailHistory: async (
    filters: Record<string, unknown> = {},
  ): Promise<unknown> => {
    try {
      const response = await api.get("/communication/history", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting email history:", error);
      throw error;
    }
  },

  // Signatures
  getSignatures: async (): Promise<unknown> => {
    try {
      const response = await api.get("/communication/signatures");
      return response.data;
    } catch (error) {
      console.error("Error getting signatures:", error);
      throw error;
    }
  },

  createSignature: async (signatureData: unknown): Promise<unknown> => {
    try {
      const response = await api.post(
        "/communication/signatures",
        signatureData,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating signature:", error);
      throw error;
    }
  },

  updateSignature: async (
    id: number | string,
    signatureData: unknown,
  ): Promise<unknown> => {
    try {
      const response = await api.put(
        `/communication/signatures/${id}`,
        signatureData,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating signature:", error);
      throw error;
    }
  },

  deleteSignature: async (id: number | string): Promise<unknown> => {
    try {
      const response = await api.delete(`/communication/signatures/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting signature:", error);
      throw error;
    }
  },

  getSignature: async (id: number | string): Promise<unknown> => {
    try {
      const response = await api.get(`/communication/signatures/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error getting signature:", error);
      throw error;
    }
  },
};

const todoAPI = {
  getTodos: async (filters: TodoFilters = {}): Promise<unknown> => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);
      if (filters.relatedType)
        params.append("relatedType", filters.relatedType);
      if (filters.relatedId)
        params.append("relatedId", String(filters.relatedId));
      if (filters.candidateId)
        params.append("candidateId", String(filters.candidateId));
      if (filters.dueDateFrom)
        params.append("dueDateFrom", filters.dueDateFrom);
      if (filters.dueDateTo) params.append("dueDateTo", filters.dueDateTo);
      if (filters.page) params.append("page", String(filters.page));
      if (filters.limit) params.append("limit", String(filters.limit));

      const response = await api.get(`/todos?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Error getting todos:", error);
      throw error;
    }
  },

  getTodoById: async (id: number | string): Promise<unknown> => {
    try {
      const response = await api.get(`/todos/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error getting todo:", error);
      throw error;
    }
  },

  createTodo: async (todoData: unknown): Promise<unknown> => {
    try {
      const response = await api.post("/todos", todoData);
      return response.data;
    } catch (error) {
      console.error("Error creating todo:", error);
      throw error;
    }
  },

  updateTodo: async (
    id: number | string,
    updateData: unknown,
  ): Promise<unknown> => {
    try {
      const response = await api.patch(`/todos/${id}`, updateData);
      return response.data;
    } catch (error) {
      console.error("Error updating todo:", error);
      throw error;
    }
  },

  deleteTodo: async (id: number | string): Promise<unknown> => {
    try {
      const response = await api.delete(`/todos/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting todo:", error);
      throw error;
    }
  },

  bulkUpdateTodos: async (
    ids: number[],
    updateData: unknown,
  ): Promise<unknown> => {
    try {
      const response = await api.put("/todos/bulk", { ids, updateData });
      return response.data;
    } catch (error) {
      console.error("Error bulk updating todos:", error);
      throw error;
    }
  },

  bulkDeleteTodos: async (ids: number[]): Promise<unknown> => {
    try {
      const response = await api.delete("/todos/bulk", { data: { ids } });
      return response.data;
    } catch (error) {
      console.error("Error bulk deleting todos:", error);
      throw error;
    }
  },

  bulkUpdateTodoStatus: async (
    ids: number[],
    status: string,
  ): Promise<unknown> => {
    try {
      const response = await api.patch("/todos/bulk/status", { ids, status });
      return response.data;
    } catch (error) {
      console.error("Error bulk updating todo status:", error);
      throw error;
    }
  },
};

// Main API export
const apiExport = {
  ...api,
  auth: authAPI,
  user: userAPI,
  config: configAPI,
  openai: openaiAPI,
  email: emailAPI,
  file: fileAPI,
  admin: adminAPI,
  health: healthAPI,
  communication: communicationAPI,
  todo: todoAPI,
  get recruiting() {
    // Lazy import to avoid circular dependency
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { recruitingAPI } = require("./recruitingApi");
    return recruitingAPI;
  },
  kanban: {
    // Ideas
    getIdeas: async (
      filters: Record<string, unknown> = {},
    ): Promise<unknown> => {
      const response = await api.get("/kanban/ideas", { params: filters });
      return response.data;
    },
    getIdea: async (id: number | string): Promise<unknown> => {
      const response = await api.get(`/kanban/ideas/${id}`);
      return response.data;
    },
    createIdea: async (data: unknown): Promise<unknown> => {
      const response = await api.post("/kanban/ideas", data);
      return response.data;
    },
    updateIdea: async (
      id: number | string,
      data: unknown,
    ): Promise<unknown> => {
      const response = await api.put(`/kanban/ideas/${id}`, data);
      return response.data;
    },
    deleteIdea: async (id: number | string): Promise<unknown> => {
      const response = await api.delete(`/kanban/ideas/${id}`);
      return response.data;
    },
    updateIdeaStage: async (
      id: number | string,
      stage: string,
    ): Promise<unknown> => {
      const response = await api.patch(`/kanban/ideas/${id}/stage`, { stage });
      return response.data;
    },
    // Bulk Idea Operations
    bulkUpdateIdeas: async (
      ids: number[],
      updateData: unknown,
    ): Promise<unknown> => {
      const response = await api.put("/kanban/ideas/bulk", { ids, updateData });
      return response.data;
    },
    bulkDeleteIdeas: async (ids: number[]): Promise<unknown> => {
      const response = await api.delete("/kanban/ideas/bulk", {
        data: { ids },
      });
      return response.data;
    },
    bulkUpdateIdeaStage: async (
      ids: number[],
      stage: string,
    ): Promise<unknown> => {
      const response = await api.patch("/kanban/ideas/bulk/stage", {
        ids,
        stage,
      });
      return response.data;
    },
    // Tasks
    createTask: async (
      ideaId: number | string,
      data: unknown,
    ): Promise<unknown> => {
      const response = await api.post(`/kanban/ideas/${ideaId}/tasks`, data);
      return response.data;
    },
    getTasks: async (ideaId: number | string): Promise<unknown> => {
      const response = await api.get(`/kanban/ideas/${ideaId}/tasks`);
      return response.data;
    },
    updateTask: async (
      id: number | string,
      data: unknown,
    ): Promise<unknown> => {
      const response = await api.put(`/kanban/tasks/${id}`, data);
      return response.data;
    },
    deleteTask: async (id: number | string): Promise<unknown> => {
      const response = await api.delete(`/kanban/tasks/${id}`);
      return response.data;
    },
    // Bulk Task Operations
    bulkUpdateTasks: async (
      ids: number[],
      updateData: unknown,
    ): Promise<unknown> => {
      const response = await api.put("/kanban/tasks/bulk", { ids, updateData });
      return response.data;
    },
    bulkDeleteTasks: async (ids: number[]): Promise<unknown> => {
      const response = await api.delete("/kanban/tasks/bulk", {
        data: { ids },
      });
      return response.data;
    },
  },
  wiki: {
    getTree: async (): Promise<unknown> => {
      const response = await api.get("/wiki/tree");
      return response.data;
    },
    createFolder: async (
      name: string,
      parent_id: number | null = null,
    ): Promise<unknown> => {
      const response = await api.post("/wiki/folders", { name, parent_id });
      return response.data;
    },
    updateFolder: async (
      id: number | string,
      update: unknown,
    ): Promise<unknown> => {
      const response = await api.put(`/wiki/folders/${id}`, update);
      return response.data;
    },
    deleteFolder: async (id: number | string): Promise<unknown> => {
      const response = await api.delete(`/wiki/folders/${id}`);
      return response.data;
    },
    createPage: async (
      title: string,
      folder_id: number | null = null,
      content: string = "",
    ): Promise<unknown> => {
      const response = await api.post("/wiki/pages", {
        title,
        folder_id,
        content,
      });
      return response.data;
    },
    getPage: async (id: number | string): Promise<unknown> => {
      const response = await api.get(`/wiki/pages/${id}`);
      return response.data;
    },
    updatePage: async (
      id: number | string,
      update: unknown,
    ): Promise<unknown> => {
      const response = await api.put(`/wiki/pages/${id}`, update);
      return response.data;
    },
    deletePage: async (id: number | string): Promise<unknown> => {
      const response = await api.delete(`/wiki/pages/${id}`);
      return response.data;
    },
    search: async (q: string): Promise<unknown> => {
      const response = await api.get(`/wiki/search?q=${encodeURIComponent(q)}`);
      return response.data;
    },
    downloadAll: async (): Promise<{ success: boolean; message: string }> => {
      try {
        const response = await api.get("/wiki/download-all", {
          responseType: "blob",
        });

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `wiki-export-${new Date().toISOString().split("T")[0]}.zip`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return { success: true, message: "Download started" };
      } catch (error) {
        console.error("Error downloading wiki:", error);
        throw error;
      }
    },
  },

  // Prompt Management API
  prompts: {
    async getAllPrompts(): Promise<unknown> {
      try {
        const response = await api.get("/admin/prompts");
        return response.data;
      } catch (error) {
        console.error("Error fetching prompts:", error);
        throw error;
      }
    },

    async getPrompt(key: string): Promise<unknown> {
      try {
        const response = await api.get(`/admin/prompts/${key}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching prompt:", error);
        throw error;
      }
    },

    async updatePrompt(key: string, value: string): Promise<unknown> {
      try {
        const response = await api.post(`/admin/prompts/${key}`, { value });
        return response.data;
      } catch (error) {
        console.error("Error updating prompt:", error);
        throw error;
      }
    },
  },

  // Onboarding API
  onboarding: {
    async register(data: {
      companyName: string;
      country: string;
      industry: string;
      adminName: string;
      adminEmail: string;
      adminPassword: string;
    }): Promise<unknown> {
      const response = await api.post("/onboarding/register", data);
      return response.data;
    },
    async getStatus(): Promise<{
      success: boolean;
      completed: boolean;
      companyId: number | null;
    }> {
      const response = await api.get("/onboarding/status");
      return response.data;
    },
    async complete(settings: Record<string, string>): Promise<unknown> {
      const response = await api.post("/onboarding/complete", { settings });
      return response.data;
    },
    async inviteTeam(
      invites: Array<{ email: string; name: string; role: string }>,
    ): Promise<unknown> {
      const response = await api.post("/onboarding/invite-team", { invites });
      return response.data;
    },
  },

  // Tenant Administration API
  tenant: {
    async getSettings(): Promise<unknown> {
      const response = await api.get("/tenant/settings");
      return response.data;
    },
    async updateSettings(data: {
      name?: string;
      country?: string;
      settings?: Record<string, string>;
    }): Promise<unknown> {
      const response = await api.put("/tenant/settings", data);
      return response.data;
    },
    async getUsers(): Promise<unknown> {
      const response = await api.get("/tenant/users");
      return response.data;
    },
    async inviteUser(data: {
      email: string;
      name: string;
      role?: string;
    }): Promise<unknown> {
      const response = await api.post("/tenant/users/invite", data);
      return response.data;
    },
    async updateUserRole(userId: number, role: string): Promise<unknown> {
      const response = await api.put(`/tenant/users/${userId}/role`, { role });
      return response.data;
    },
    async deactivateUser(userId: number): Promise<unknown> {
      const response = await api.put(`/tenant/users/${userId}/deactivate`);
      return response.data;
    },
    async activateUser(userId: number): Promise<unknown> {
      const response = await api.put(`/tenant/users/${userId}/activate`);
      return response.data;
    },
    async getAnalytics(): Promise<unknown> {
      const response = await api.get("/tenant/analytics");
      return response.data;
    },
    async uploadLogo(file: File): Promise<unknown> {
      const formData = new FormData();
      formData.append("logo", file);
      const response = await api.post("/tenant/settings/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    async deleteLogo(): Promise<unknown> {
      const response = await api.delete("/tenant/settings/logo");
      return response.data;
    },
  },

  // Subscription Management API
  subscription: {
    async checkSubscription(): Promise<unknown> {
      try {
        const response = await api.get("/subscription/check");
        return response.data;
      } catch (error) {
        console.error("Error checking subscription:", error);
        throw error;
      }
    },

    async createCheckout(
      options: Record<string, unknown> = {},
    ): Promise<unknown> {
      try {
        const response = await api.post(
          "/subscription/create-checkout",
          options,
        );
        return response.data;
      } catch (error) {
        console.error("Error creating checkout:", error);
        throw error;
      }
    },

    async createPortal(
      options: Record<string, unknown> = {},
    ): Promise<unknown> {
      try {
        const response = await api.post("/subscription/create-portal", options);
        return response.data;
      } catch (error) {
        console.error("Error creating portal:", error);
        throw error;
      }
    },
  },

  // Feedback API
  feedback: {
    async submitFeedback(
      data: SubmitFeedbackPayload,
    ): Promise<FeedbackSubmitResponse> {
      try {
        const response = await api.post("/feedback", data);
        return response.data;
      } catch (error) {
        console.error("Error in submitFeedback:", error);
        throw error;
      }
    },
    async getFeedbackList(
      params: {
        type?: string;
        status?: string;
        page?: number;
        limit?: number;
      } = {},
    ): Promise<FeedbackListResponse> {
      try {
        const response = await api.get("/feedback", { params });
        return response.data;
      } catch (error) {
        console.error("Error in getFeedbackList:", error);
        throw error;
      }
    },
    async getFeedbackStats(): Promise<FeedbackStatsResponse> {
      try {
        const response = await api.get("/feedback/stats");
        return response.data;
      } catch (error) {
        console.error("Error in getFeedbackStats:", error);
        throw error;
      }
    },
    async getFeedbackById(id: number): Promise<FeedbackDetailResponse> {
      try {
        const response = await api.get(`/feedback/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error in getFeedbackById:", error);
        throw error;
      }
    },
    async updateFeedback(
      id: number,
      data: UpdateFeedbackPayload,
    ): Promise<FeedbackDetailResponse> {
      try {
        const response = await api.put(`/feedback/${id}`, data);
        return response.data;
      } catch (error) {
        console.error("Error in updateFeedback:", error);
        throw error;
      }
    },
  },

  // Release Notes API
  releaseNotes: {
    async getPublished() {
      const response = await api.get("/release-notes");
      return response.data;
    },
    async getLatest() {
      const response = await api.get("/release-notes/latest");
      return response.data;
    },
    async getSeenStatus() {
      const response = await api.get("/release-notes/user/seen-status");
      return response.data;
    },
    async markSeen(version: string) {
      const response = await api.post("/release-notes/user/mark-seen", {
        version,
      });
      return response.data;
    },
    // Admin endpoints
    async generateFromADO(options?: { sinceDate?: string; version?: string }) {
      const response = await api.post(
        "/release-notes/admin/generate",
        options || {},
      );
      return response.data;
    },
    async getAll() {
      const response = await api.get("/release-notes/admin/all");
      return response.data;
    },
    async create(data: {
      version: string;
      title: string;
      content: string;
      categories?: string[];
    }) {
      const response = await api.post("/release-notes", data);
      return response.data;
    },
    async update(
      id: number,
      data: {
        version?: string;
        title?: string;
        content?: string;
        categories?: string[];
      },
    ) {
      const response = await api.put(`/release-notes/${id}`, data);
      return response.data;
    },
    async publish(id: number) {
      const response = await api.post(`/release-notes/${id}/publish`);
      return response.data;
    },
    async remove(id: number) {
      const response = await api.delete(`/release-notes/${id}`);
      return response.data;
    },
  },
};

// Export raw axios instance for direct HTTP calls
export { api as axiosInstance };

export default apiExport;
